import {
  PDP_REVIEWS_SECTION_ID,
  PDP_SIMILAR_PRODUCTS_SECTION_ID,
  PDP_SUPPLIER_CAPABILITIES_SECTION_ID,
} from '@/shared/constants/sourcing-marketplace';
import { SupplierReviewSummaryResponse } from '@/shared/types/api/catalog';
import { CatalogCompanyType, CatalogItem, SupplierReview } from '@/shared/types/catalog';
import { getCreatorHubProductDetailsPagePath, getSupplierName } from '@/shared/utils/sourcing-marketplace-util';
import Image from 'next/image';
import React, { useEffect, useState } from 'react';
import { getCatalogItems } from '@/lib/service/catalog';
import dynamic from 'next/dynamic';
import { isEmpty, size } from 'lodash-es';
import './index.less';
import cN from 'classnames';
import { useIsMobileURL } from '@/shared/hooks';

interface IProps {
  product: CatalogItem;
  reviews: SupplierReview[];
  reviewSummary: SupplierReviewSummaryResponse;
}

const SuggestedProducts = dynamic(() => import('../SuggestedProducts'));
const SupplierReviews = dynamic(() => import('../../Common/SupplierReviews'));
const ExplorePietraServicesBanner = dynamic(() => import('../../Common/ExplorePietraServicesBanner'));

const ProductDetailsPanel = ({ product, reviews, reviewSummary }: IProps) => {
  const isMobileURL = useIsMobileURL();
  const supplier = product.catalogCompany;
  const customizations = supplier?.productionCustomizations;
  const isServices = supplier.type === CatalogCompanyType.SPECIALIST;

  const [similarCategoryProducts, setSimilarCategoryProducts] = useState<CatalogItem[]>([]);
  const [similarSupplierProducts, setSimilarSupplierProducts] = useState<CatalogItem[]>([]);

  const ProductName = () => (
    <div className="section">
      <div className="header">
        <h2 className="title">{product.name}</h2>
      </div>
      <div className="text description">{product?.meta?.description}</div>
    </div>
  );

  const supplierName = getSupplierName(supplier);

  const SupplierCapabilitiesSection = () =>
    !isEmpty(customizations) &&
    !isServices && (
      <div className="section" id={PDP_SUPPLIER_CAPABILITIES_SECTION_ID}>
        <div className="header">
          <h2 className="title">Supplier capabilities of {supplierName}</h2>
          <span className="subtitle">
            These customizations will be available to you when ordering a production run from this supplier.
          </span>
        </div>
        <div className="grid">
          {customizations?.map((customization) => {
            return (
              <div className="customization" key={customization.image}>
                {customization.image && (
                  <Image src={customization.image} width={62} height={62} alt={'The icon of + ' + customization.text} />
                )}
                <span>{customization.text}</span>
              </div>
            );
          })}
        </div>
      </div>
    );

  const ReviewsSection = () => (
    <div className="section" id={PDP_REVIEWS_SECTION_ID}>
      <div className="header">
        <h2 className="title">Reviews for {supplierName}</h2>
      </div>
      <SupplierReviews
        reviewsList={reviews}
        reviewSummary={reviewSummary}
        total={size(reviews)}
        page={0}
        supplierName={supplierName}
      />
    </div>
  );

  useEffect(() => {
    const fetachData = async () => {
      const [{ list: similarCategoryProducts }, { list: similarSupplierProducts }] = await Promise.all([
        getCatalogItems({
          catalogCategoryId: [product.catalogCategoryId],
          size: 12,
          page: 0,
        }),
        getCatalogItems({
          catalogCompanyId: [product.catalogCompany?.id],
          catalogCategoryId: [product.catalogCategoryId],
          size: 12,
          page: 0,
        }),
      ]);
      // 过滤掉当前产品
      setSimilarCategoryProducts(similarCategoryProducts.filter((item) => item.id !== product.id));
      setSimilarSupplierProducts(similarSupplierProducts.filter((item) => item.id !== product.id));
    };

    if (product?.id) fetachData().catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  const renderSameFactoryProductsSection = (
    <div className="section" id={PDP_SIMILAR_PRODUCTS_SECTION_ID}>
      <div className="header">
        <h2 className="title">Other products from {supplierName}</h2>
      </div>
      <SuggestedProducts product={product} products={similarSupplierProducts} />
    </div>
  );

  const renderSimilarProductsSection = (
    <div className="section" id={PDP_SIMILAR_PRODUCTS_SECTION_ID}>
      <div className="header">
        <h2 className="title">Similar products to the {product.name}</h2>
      </div>
      <SuggestedProducts product={product} products={similarCategoryProducts} />
    </div>
  );

  return (
    <div className={cN('product-details-panel', { 'product-details-panel-mobile': isMobileURL })}>
      <ProductName />
      <SupplierCapabilitiesSection />
      {!isEmpty(similarSupplierProducts) && renderSameFactoryProductsSection}
      {!isEmpty(similarCategoryProducts) && renderSimilarProductsSection}
      <ExplorePietraServicesBanner
        signupRedirect={getCreatorHubProductDetailsPagePath(product.id)}
        utmMedium="product_details_page"
      />
      <ReviewsSection />
    </div>
  );
};

export default ProductDetailsPanel;
